import { RedoTextareaInputField } from "@redotech/arbiter/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { t, useTranslation } from "@redotech/redo-i18n";
import Clock3Icon from "@redotech/redo-icons/clock.svg";
import CompassIcon from "@redotech/redo-icons/compass-01_filled.svg";
import FileQuestionIcon from "@redotech/redo-icons/file-question-01_filled.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import SettingsIcon from "@redotech/redo-icons/settings.svg";
import ShapesIcon from "@redotech/redo-icons/shapes.svg";
import UsersIcon from "@redotech/redo-icons/users-01.svg";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import classNames from "classnames";
import {
  memo,
  useCallback,
  useContext,
  useState,
  type JSXElementConstructor,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOnboardingTracking } from "../../hooks/use-onboarding-tracking";
import { useProduct } from "../../hooks/use-product";
import { OnboardingV2RpcClientContext } from "../../rpc-client";
import * as stepShellStyles from "../layouts/step-shell.module.css";
import { AiStarIcon } from "./ai-star-icon";
import { DecryptedText } from "./decrypted-text";
import * as styles from "./exit-onboarding-button.module.css";

const DEFAULT_MODAL_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/onboarding-v2-returns.webp";
const FEEDBACK_FORM_KEY = "exit-feedback";

interface ExitReasonOption {
  id: string;
  label: string;
  Icon: JSXElementConstructor<any>;
  hidden: boolean;
}

const EXIT_REASON_OPTIONS = [
  {
    id: "need-more-time",
    label: "I need more time",
    Icon: Clock3Icon,
    hidden: false,
  },
  {
    id: "need-more-information",
    label: "I'm still gathering information",
    Icon: FileQuestionIcon,
    hidden: false,
  },
  {
    id: "need-approval",
    label: "I need approval from my team",
    Icon: UsersIcon,
    hidden: false,
  },
  {
    id: "need-help",
    label: "I want help from Redo",
    Icon: HelpCircleIcon,
    hidden: false,
  },
  {
    id: "need-rma-help",
    label: "I need help with my RMA setup",
    Icon: SettingsIcon,
    hidden: true,
  },
  {
    id: "just-exploring",
    label: "I'm just exploring for now",
    Icon: CompassIcon,
    hidden: false,
  },
  { id: "other", label: "Other", Icon: ShapesIcon, hidden: false },
] as const satisfies readonly ExitReasonOption[];

export type ExitReasonId = (typeof EXIT_REASON_OPTIONS)[number]["id"];

const NEED_HELP_REASON_ID = "need-help" satisfies ExitReasonId;
const NEED_RMA_HELP_REASON_ID = "need-rma-help" satisfies ExitReasonId;
const SCHEDULE_CALL_REASON_IDS = new Set<ExitReasonId>([
  NEED_HELP_REASON_ID,
  NEED_RMA_HELP_REASON_ID,
]);

// Option labels stay English because they are saved as feedback data.
function getExitReasonLabel(id: ExitReasonId): string {
  switch (id) {
    case "need-more-time":
      return t("I need more time");
    case "need-more-information":
      return t("I'm still gathering information");
    case "need-approval":
      return t("I need approval from my team");
    case "need-help":
      return t("I want help from Redo");
    case "need-rma-help":
      return t("I need help with my RMA setup");
    case "just-exploring":
      return t("I'm just exploring for now");
    case "other":
      return t("Other", { context: "exit reason" });
  }
}

function getExitReasonPlaceholder(id: ExitReasonId): string {
  switch (id) {
    case "need-more-time":
      return t("Anything that would help when you come back");
    case "need-more-information":
      return t("What you still need to find or confirm");
    case "need-approval":
      return t("Who or what you're waiting on");
    case "need-help":
      return t("What you want help with");
    case "need-rma-help":
      return t("Describe your system and what you need");
    case "just-exploring":
      return t("What you're still weighing");
    case "other":
      return t("Anything we should know");
  }
}

export const FeedbackModal = memo(function FeedbackModal({
  isOpen,
  onClose,
  onConfirm,
  initialReasonId,
  hideAskRedo,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  initialReasonId?: ExitReasonId;
  hideAskRedo?: boolean;
}) {
  const { t } = useTranslation();
  const rpcClient = useContext(OnboardingV2RpcClientContext);
  const product = useProduct();
  const navigate = useNavigate();
  const { open: openAiPanel } = useAiSlidePanel();
  const { stepPath } = useParams<{ stepPath: string }>();
  const { trackExitReason } = useOnboardingTracking();

  const startAtFollowup = !!initialReasonId;
  const [isSaving, setIsSaving] = useState(false);
  const [modalStep, setModalStep] = useState<"reason-select" | "followup">(
    startAtFollowup ? "followup" : "reason-select",
  );
  const [selectedReasonId, setSelectedReasonId] = useState<ExitReasonId | null>(
    initialReasonId ?? null,
  );
  const [followUpResponse, setFollowUpResponse] = useState("");

  const resetState = useCallback(() => {
    setIsSaving(false);
    setModalStep(startAtFollowup ? "followup" : "reason-select");
    setSelectedReasonId(initialReasonId ?? null);
    setFollowUpResponse("");
  }, [startAtFollowup, initialReasonId]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleContinue = useCallback(() => {
    if (!selectedReasonId) {
      return;
    }
    setModalStep("followup");
  }, [selectedReasonId]);

  const handleBack = useCallback(() => {
    if (startAtFollowup) {
      handleClose();
    } else {
      resetState();
    }
  }, [startAtFollowup, handleClose, resetState]);

  const selectedReason = selectedReasonId
    ? (EXIT_REASON_OPTIONS.find((option) => option.id === selectedReasonId) ??
      null)
    : null;
  const currentStep = product?.steps.find((step) => step.path === stepPath);
  const headerImageUrl = product?.imageUrl ?? DEFAULT_MODAL_IMAGE_URL;
  const showFollowUpStep = modalStep === "followup" && !!selectedReason;
  const showNeedHelpFollowUp =
    showFollowUpStep &&
    !!selectedReason &&
    SCHEDULE_CALL_REASON_IDS.has(selectedReason.id);

  const handleConfirm = useCallback(async () => {
    const trimmedResponse = followUpResponse.trim();
    if (!trimmedResponse || !selectedReason) {
      return;
    }

    const wantsScheduleCall = SCHEDULE_CALL_REASON_IDS.has(selectedReason.id);
    const scheduleCallPath =
      product && stepPath ? `/${product.id}/schedule-call` : null;

    setIsSaving(true);
    try {
      if (rpcClient && product && currentStep) {
        const stepIndex = product.steps.findIndex(
          (s) => s.id === currentStep.id,
        );
        trackExitReason({
          stepId: currentStep.id,
          stepIndex,
          reasonId: selectedReason.id,
          reasonLabel: selectedReason.label,
          responseText: trimmedResponse,
        });
        await rpcClient.saveProgress({
          productId: product.id,
          currentStepId: currentStep.id,
          sourceStepId: FEEDBACK_FORM_KEY,
          status: wantsScheduleCall ? "help-requested" : undefined,
          stepData: {
            reasonId: selectedReason.id,
            reasonLabel: selectedReason.label,
            responseText: trimmedResponse,
            stepId: currentStep.id,
            submittedAt: new Date().toISOString(),
          },
        });
      }
    } catch {
      // Do not block the flow if feedback persistence fails.
    } finally {
      setIsSaving(false);
      resetState();
      if (wantsScheduleCall && scheduleCallPath && product && stepPath) {
        onClose();
        navigate(scheduleCallPath, {
          state: { from: `/${product.id}/${stepPath}` },
        });
      } else {
        onConfirm();
      }
    }
  }, [
    currentStep,
    followUpResponse,
    navigate,
    product,
    rpcClient,
    selectedReason,
    stepPath,
    resetState,
    onClose,
    onConfirm,
    trackExitReason,
  ]);

  return (
    <RedoModal
      hideFooter
      hideHeader
      isOpen={isOpen}
      modalSize="sm"
      muiClassName={styles.exitModalRoot}
      noPadding
      onModalCloseRequested={handleClose}
      title={t("Before you go")}
    >
      <div className={styles.modalImageWrapper}>
        <img alt="" className={styles.modalHeaderImage} src={headerImageUrl} />
        <div className={styles.modalImageOverlay} />
        <div className={styles.modalImageLabel}>
          <Text as="span" className={styles.modalImageTitle} lineHeight="1">
            <DecryptedText
              animateOn="view"
              key={
                showFollowUpStep && selectedReason
                  ? selectedReason.id
                  : "before-you-go"
              }
              sequential
              speed={40}
              text={
                showFollowUpStep && selectedReason
                  ? getExitReasonLabel(selectedReason.id)
                  : t("Before you go")
              }
            />
          </Text>
          {!showFollowUpStep && (
            <p className={styles.modalImageSubheading}>
              {t("Choose the main reason you're leaving onboarding right now.")}
            </p>
          )}
        </div>
      </div>

      <div className={styles.modalContentBody}>
        {showFollowUpStep && selectedReason ? (
          <RedoTextareaInputField
            autoFocus
            className={styles.followUpInput}
            containerHeight="120px"
            fullWidth
            label={t("Tell us more")}
            placeholder={getExitReasonPlaceholder(selectedReason.id)}
            setValue={setFollowUpResponse}
            value={followUpResponse}
          />
        ) : (
          <div className={styles.reasonList}>
            {EXIT_REASON_OPTIONS.filter((o) => !o.hidden).map((option) => (
              // design-allow: selectable reason card, not a RedoButton
              <button
                className={classNames(
                  styles.reasonCard,
                  selectedReasonId === option.id && styles.reasonCardSelected,
                )}
                key={option.id}
                onClick={() => setSelectedReasonId(option.id)}
                type="button"
              >
                <div className={styles.reasonLabel}>
                  <div className={styles.reasonIconWrap}>
                    <option.Icon className={styles.reasonIcon} />
                  </div>
                  <span className={styles.reasonTitle}>
                    {getExitReasonLabel(option.id)}
                  </span>
                </div>
                <div
                  className={classNames(
                    styles.reasonIndicator,
                    selectedReasonId === option.id &&
                      styles.reasonIndicatorSelected,
                  )}
                >
                  <div className={styles.reasonIndicatorDot} />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className={styles.actionsRow}>
          {/* design-allow: step-shell action, not a RedoButton */}
          <button
            className={classNames(
              stepShellStyles.continueButton,
              styles.exitModalActionBtn,
            )}
            disabled={isSaving}
            onClick={showFollowUpStep ? handleBack : handleClose}
            type="button"
          >
            {showFollowUpStep
              ? startAtFollowup
                ? t("Cancel")
                : t("Back")
              : t("Stay here")}
          </button>
          {showFollowUpStep ? (
            <div className={styles.actionsRowEnd}>
              {!hideAskRedo && (
                // design-allow: Ask Redo action, not a RedoButton
                <button
                  className={styles.askRedoBlackBtn}
                  disabled={isSaving}
                  onClick={() => {
                    resetState();
                    onClose();
                    openAiPanel({
                      prefillInput: followUpResponse.trim() || undefined,
                      autoSubmit: false,
                    });
                  }}
                  type="button"
                >
                  <AiStarIcon className={styles.askRedoBlackBtnIcon} />
                  {t("Ask Redo")}
                </button>
              )}
              {/* design-allow: step-shell confirm, not a RedoButton */}
              <button
                className={classNames(
                  stepShellStyles.continueButton,
                  styles.exitModalActionBtn,
                )}
                disabled={isSaving || followUpResponse.trim().length === 0}
                onClick={handleConfirm}
                type="button"
              >
                {isSaving
                  ? showNeedHelpFollowUp
                    ? t("Saving...")
                    : t("Exiting...")
                  : showNeedHelpFollowUp
                    ? t("Schedule a call", { context: "book a call with Redo" })
                    : t("Exit to Dashboard")}
              </button>
            </div>
          ) : (
            <>
              {/* design-allow: step-shell continue, not a RedoButton */}
              <button
                className={classNames(
                  stepShellStyles.continueButton,
                  styles.exitModalActionBtn,
                )}
                disabled={isSaving || !selectedReasonId}
                onClick={handleContinue}
                type="button"
              >
                {t("Continue")}
              </button>
            </>
          )}
        </div>
      </div>
    </RedoModal>
  );
});
